import {Component, Input, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToasterService} from 'angular2-toaster';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {TranslocoService} from '@jsverse/transloco';

interface FmEntry {
    name: string;
    rights: string;
    date: string;
    size: number;
    type: string;
}

/**
 * kobe-ac 書類 (issue #161 タスク5)。顧客ユーザ向けの読取専用ビュー。
 * backend FileController は OUser.customer.uuid で customers/<uuid>/ にスコープするため、
 * 顧客ユーザは自分のフォルダ (売買台帳 PDF 等) だけが見える。ここでは一覧・フォルダ移動・
 * 認証付きダウンロードのみを提供し、作成/削除/アップロードは行わない。
 *
 * ダウンロードは素の <a href> だと Authorization ヘッダが付かず backend の
 * beginScope (ヘッダのみ参照) がスコープしないため、必ず HttpClient (JWT
 * インターセプタがヘッダ付与) の blob 取得にする。
 */
@Component({
    standalone: false,
    selector: 'kobeac-document',
    templateUrl: 'document.component.html',
})
export class KobeacDocumentComponent implements OnInit {
    private base = ActivePromotionConfig.SERVER_URL + '/fm';
    /** ダッシュボード等に埋め込むと true。外側の container を外し、顧客未紐付け時はカードごと隠す。 */
    @Input() embedded = false;
    path = '/';
    entries: FmEntry[] = [];
    loading = false;
    // 顧客(AC_Customer)が紐付いているか。未紐付けだと backend のスコープが効かず
    // 全顧客フォルダが見えてしまうため、未紐付け時は一覧を取得せず何も表示しない。
    hasCustomer = false;
    checkedCustomer = false;

    constructor(private http: HttpClient, private toasterService: ToasterService, private transloco: TranslocoService) {
    }

    ngOnInit(): void {
        this.http.get(ActivePromotionConfig.SERVER_URL + '/rest/user').subscribe({
            next: (user: any) => {
                this.hasCustomer = !!(user && user.customer);
                this.checkedCustomer = true;
                if (this.hasCustomer) {
                    this.list();
                } else {
                    this.entries = [];
                }
            },
            error: () => {
                this.checkedCustomer = true;
                this.hasCustomer = false;
                this.entries = [];
            },
        });
    }

    get breadcrumbs(): { name: string; path: string }[] {
        const parts = this.path.split('/').filter((p) => p);
        const crumbs = [{name: this.transloco.translate('kobeac.documents'), path: '/'}];
        let acc = '';
        for (const p of parts) {
            acc += '/' + p;
            crumbs.push({name: p, path: acc});
        }
        return crumbs;
    }

    list(): void {
        // 顧客未紐付けのときは一覧を取得しない (全フォルダ露出防止)。
        if (!this.hasCustomer) {
            this.entries = [];
            return;
        }
        this.loading = true;
        this.http.post(this.base, {params: {mode: 'list', onlyFolders: false, path: this.path}}).subscribe({
            next: (res: any) => {
                this.entries = (res && res.result) ? res.result : [];
                this.entries.sort((a: FmEntry, b: FmEntry) => {
                    if (a.type != b.type) return a.type == 'dir' ? -1 : 1;
                    return a.name.localeCompare(b.name);
                });
                this.loading = false;
            },
            error: () => {
                this.toasterService.pop('error', this.transloco.translate('kobeac.documents'), this.transloco.translate('kobeac.list_load_failed'));
                this.loading = false;
            },
        });
    }

    open(entry: FmEntry): void {
        if (entry.type == 'dir') {
            this.path = (this.path == '/' ? '' : this.path) + '/' + entry.name;
            this.list();
        } else {
            this.download(entry);
        }
    }

    goto(path: string): void {
        this.path = path;
        this.list();
    }

    private childPath(name: string): string {
        return (this.path == '/' ? '' : this.path) + '/' + name;
    }

    /** JWT インターセプタ付きの blob 取得でダウンロードする (認証＋顧客スコープが効く)。 */
    download(entry: FmEntry): void {
        this.http.get(this.base, {params: {path: this.childPath(entry.name)}, responseType: 'blob'}).subscribe({
            next: (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = entry.name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.documents'), this.transloco.translate('kobeac.download_failed')),
        });
    }

    convertSize(size: number): string {
        return size < 1024000 ? (size / 1024).toFixed(2) + ' KB' : (size / 1024000).toFixed(2) + ' MB';
    }
}
